import React, { useState } from "react";
import BottomSheet from "../BottomSheet";
import Button from "../Button";
import Card from "../Card/Card";
import {
  MoreVertical,
  Home,
  LucidePencil,
  LucideTrash,
  Briefcase,
  MapPin,
} from "lucide-react";
import { data, useNavigate, useParams } from "react-router-dom";
import { AddressListResponse } from "src/types/location.types";
import Popover from "src/components/common/Popover";
import { deleteAddressRequest } from "src/redux/slices/locationSlice";
import { useAppDispatch } from "src/redux/hooks";
interface AddAddressBottomSheetProps {
  showBottomSheet: boolean;
  setShowBottomSheet: (show: boolean) => void;
  addressList: AddressListResponse[];
  height?: string;
  onAddressSelect: (address: AddressListResponse) => void;
}

/*const addressList: any[] = [
  {
    id: 1,
    name: "Home",
    address:
      "123 Main St, Anytown, USA\n Anytown, USA 12345 karnataka rhfrbfjke bjfeb hbejwbc",
  },
];*/

const AddAddressBottomSheet: React.FC<AddAddressBottomSheetProps> = React.memo(
  ({
    showBottomSheet,
    setShowBottomSheet,
    addressList,
    height,
    onAddressSelect,
  }: AddAddressBottomSheetProps) => {
    const [pincode, setPincode] = useState("");
    const [isPincodeLoading, setIsPincodeLoading] = useState(false);
    const navigate = useNavigate();
    const params = useParams();
    const supportProgramKey = params["support-program-key"];
    const dispatch = useAppDispatch();

    const onAddNewAddress = () => {
      navigate(`/app/${supportProgramKey}/book-lab-test/add-complete-address`);
    };

    const onDeleteAddress = (addressId: string) => {
      new Promise((resolve, reject) => {
        dispatch(
          deleteAddressRequest({
            resolve,
            reject,
            payload: { address_id: addressId },
          })
        );
      })
        .then((res) => {
          navigate(`/app/${supportProgramKey}/book-lab-test/checkout`, {
            state: { isRefetchCartDetails: true },
          });
        })
        .catch((err) => {
          console.error("Error deleting address", err);
        });
    };

    const onEditAddress = (address: AddressListResponse) => {
      // Navigate back to map screen with selected location and address
      navigate(`/app/${supportProgramKey}/book-lab-test/add-complete-address`, {
        state: {
          locationToEdit: {
            lat: address.latitude,
            lng: address.longitude,
          },
          addressToEdit: {
            pincode: address.pincode,
            //houseNumber: address.address,
            street: address.street,
            formatted_address: address.address,
            address_type: address.address_type,
            //state: address.state,
          },
          addressId: address.patient_address_rel_id,
        },
      });
    };

    const hasAddress = addressList?.length > 0;

    return (
      <BottomSheet
        title="Select Address"
        show={showBottomSheet}
        handleClose={() => setShowBottomSheet(false)}
        showCloseButton
      >
        <div className="relative w-full p-4">
          {hasAddress ? (
            <AddressList
              addressList={addressList}
              onAddNewAddress={onAddNewAddress}
              onAddressSelect={onAddressSelect}
              onEditAddress={onEditAddress}
              onDeleteAddress={onDeleteAddress}
            />
          ) : (
            <NoAddress onAddNewAddress={onAddNewAddress} />
          )}
        </div>
      </BottomSheet>
    );
  }
);

export default AddAddressBottomSheet;

const NoAddress: React.FC<{ onAddNewAddress: () => void }> = ({
  onAddNewAddress,
}) => {
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="text-sm text-gray-500 text-center py-4">
        No address present. Please add a new address.
      </div>
      <Button rounded="full" fullWidth onClick={onAddNewAddress}>
        Add Address
      </Button>
    </div>
  );
};

const AddressList: React.FC<{
  addressList: AddressListResponse[];
  onAddNewAddress: () => void;
  onAddressSelect: (address: AddressListResponse) => void;
  onEditAddress: (address: AddressListResponse) => void;
  onDeleteAddress: (addressId: string) => void;
}> = ({
  addressList,
  onAddNewAddress,
  onAddressSelect,
  onEditAddress,
  onDeleteAddress,
}) => {
  const icons = {
    Home: <Home className="w-5 h-5" />,
    Work: <Briefcase className="w-5 h-5" />,
    Other: <MapPin className="w-5 h-5" />,
  };
  return (
    <div className="">
      <div className="flex justify-end pb-4">
        <button
          className="text-sm text-green-500 px-2 py-1"
          onClick={onAddNewAddress}
        >
          Add New
        </button>
      </div>
      <div className="flex flex-col gap-4">
        {addressList?.map((address, index) => (
          <div onClick={() => onAddressSelect(address)}>
            <Card
              title={address.address_type}
              key={index}
              icon={icons[address.address_type as keyof typeof icons]}
              rightIcon={
                <div onClick={(e) => e.stopPropagation()}>
                  <Popover
                    label={
                      <div className="w-6 h-6 flex items-center justify-center">
                        <MoreVertical className="w-4 h-4" />
                      </div>
                    }
                  >
                    <ul className="w-full">
                      <li className="w-full block">
                        <button
                          type="button"
                          className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditAddress(address);
                          }}
                        >
                          <LucidePencil className="w-4 h-4" /> Edit
                        </button>
                      </li>
                      <li className="w-full block">
                        <button
                          type="button"
                          className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteAddress(address.patient_address_rel_id);
                          }}
                        >
                          <LucideTrash className="w-4 h-4" /> Delete
                        </button>
                      </li>
                    </ul>
                  </Popover>
                </div>
              }
              description={address.address}
              stackTitleAndDescription={true}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
